import { COUNTRY_CODE } from '@cemex-core/cmx-api';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { State, getLanguage, getLegalEntity } from '@cemex/cmx-client-layout';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { getCurrentLanguageWithLocaleInfo } from '@ordersnproducts/utils/utils';
import { Features } from '@ordersnproducts/features';
import { CX_INPUT_TIME_FORMAT_TOKEN } from 'app/shared/cx-ui/cx-input-time/cx-input-time.config';
import { of } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';

export const cxTimeFormatProvider = {
  provide: CX_INPUT_TIME_FORMAT_TOKEN,
  deps: [Store, FeatureToggleService, TranslateService],
  useFactory: (store: Store<State>, featureToggle: FeatureToggleService, ts: TranslateService) => {
    return of('24h').pipe(
      switchMap((timeFormat) => {
        if (featureToggle.isFeatureEnabled(Features.TIME_FORMAT_BY_DEFAULT_UI)) {
          return store.pipe(
            select(getCurrentLanguageWithLocaleInfo),
            map((defaultLang) => {
              if (defaultLang === undefined) {
                console.warn('☸ Time format by Default Language is enabled, but missing match for language');
                return timeFormat;
              }

              if (defaultLang['formatTime'] === undefined) {
                console.warn('☸ Time format by Default Language is enabled, but missing a key "timeFormat"');
                return timeFormat;
              }
              /**
               * defaultLang.formatTime from default language looks like "HH:MM A" or "hh:mm a" where "A" / "a"
               * letter corresponding 12h format
               */
              if (defaultLang.formatTime.includes('A') || defaultLang.formatTime.includes('a')) {
                return '12h';
              } else {
                return '24h';
              }
            }),
          );
        }

        return of(timeFormat);
      }),
      switchMap((timeFormat) => {
        if (featureToggle.isFeatureEnabled(Features.TIME_FORMAT_BY_LANG_UI)) {
          return store.pipe(
            select(getLanguage),
            switchMap((code) => ts.getTranslation(code)),
            map((lang) => {
              if (lang['global.cwcFormatTime'] === undefined) {
                console.warn(
                  '☸ Time format by Language is enabled, but missing translation key "global.cwcFormatTime"',
                );
                return timeFormat;
              }

              return lang['global.cwcFormatTime'];
            }),
          );
        }

        return of(timeFormat);
      }),
      switchMap((timeFormat) => {
        if (featureToggle.isFeatureEnabled(Features.TIME_FORMAT_BY_COUNTRY_UI)) {
          return store.pipe(
            select(getLegalEntity),
            filter((legalEntity) => !!legalEntity),
            map((legalEntity) => legalEntity.countryCode.trim()),
            map((country) => {
              switch (country) {
                case COUNTRY_CODE.MEXICO:
                case COUNTRY_CODE.USA: {
                  return '12h';
                }

                default: {
                  return timeFormat;
                }
              }
            }),
          );
        }

        return of(timeFormat);
      }),
      distinctUntilChanged(),
      shareReplay(),
    );
  },
};
